import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { Sidebar } from "primereact/sidebar";
import { Form, Input, Select, Checkbox, InputNumber } from "antd";
import { useFormik } from "formik";
import * as yup from "yup";
import { useCompetingAgents } from "../hooks/useCompetingAgent";
import { FilterMatchMode } from "primereact/api";
import { IoIosSearch } from "react-icons/io";
import { HideSortIcons } from "../utils/tableStyles";

const validationSchema = yup.object({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  middleName: yup.string().optional(),
  nameSuffix: yup.string().optional(),
  title: yup.string().optional(),
  homeAddress: yup.string().optional(),
  officeAddress: yup.string().optional(),
  notes: yup.string().optional(),
  residentOwner: yup.boolean().optional(),
  residentTenant: yup.boolean().optional(),
  ownsOtherHomes: yup.boolean().optional(),
  agentOnBoard: yup.boolean().optional(),
  spouseOnBoard: yup.boolean().optional(),
  currentForSaleListing: yup.number().optional().default(0),
  currentForRentListing: yup.number().optional().default(0),
  closedSellerTransactions2025: yup.number().optional().default(0),
  closedSellerTransactions2024: yup.number().optional().default(0),
  closedSellerTransactions2023: yup.number().optional().default(0),
  closedSellerTransactions2022: yup.number().optional().default(0),
  closedBuyerTransactions2025: yup.number().optional().default(0),
  closedBuyerTransactions2024: yup.number().optional().default(0),
  closedBuyerTransactions2023: yup.number().optional().default(0),
  closedBuyerTransactions2022: yup.number().optional().default(0),
  closedLandlordTransactions2025: yup.number().optional().default(0),
  closedLandlordTransactions2024: yup.number().optional().default(0),
  closedLandlordTransactions2023: yup.number().optional().default(0),
  closedLandlordTransactions2022: yup.number().optional().default(0),
  closedTenantTransactions2025: yup.number().optional().default(0),
  closedTenantTransactions2024: yup.number().optional().default(0),
  closedTenantTransactions2023: yup.number().optional().default(0),
  closedTenantTransactions2022: yup.number().optional().default(0),
});

export const CompetingAgentManagement = () => {
  const {
    agents,
    isLoading,
    createAgent,
    updateAgent,
    deleteAgent,
    createAgentLoading,
    updateAgentLoading,
    deleteAgentLoading,
  } = useCompetingAgents();

  const [visible, setVisible] = useState(false);
  const [visibleRight, setVisibleRight] = useState(false);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    nameSuffix: "",
    title: "",
    homeAddress: "",
    officeAddress: "",
    notes: "",
    residentOwner: false,
    residentTenant: false,
    ownsOtherHomes: false,
    agentOnBoard: false,
    spouseOnBoard: false,
    currentForSaleListing: 0,
    currentForRentListing: 0,
    closedSellerTransactions2025: 0,
    closedSellerTransactions2024: 0,
    closedSellerTransactions2023: 0,
    closedSellerTransactions2022: 0,
    closedBuyerTransactions2025: 0,
    closedBuyerTransactions2024: 0,
    closedBuyerTransactions2023: 0,
    closedBuyerTransactions2022: 0,
    closedLandlordTransactions2025: 0,
    closedLandlordTransactions2024: 0,
    closedLandlordTransactions2023: 0,
    closedLandlordTransactions2022: 0,
    closedTenantTransactions2025: 0,
    closedTenantTransactions2024: 0,
    closedTenantTransactions2023: 0,
    closedTenantTransactions2022: 0,
  });

  const agentFormik = useFormik({
    enableReinitialize: true,
    initialValues: selectedAgent,
    validationSchema,
    onSubmit: (values) => {
      if (editingId != null) {
        updateAgent({ id: editingId, agentData: values });
      } else {
        createAgent(values);
      }
      setVisible(false);
      agentFormik.resetForm();
      setSelectedAgent({
        firstName: "",
        middleName: "",
        lastName: "",
        nameSuffix: "",
        title: "",
        homeAddress: "",
        officeAddress: "",
        notes: "",
        residentOwner: false,
        residentTenant: false,
        ownsOtherHomes: false,
        agentOnBoard: false,
        spouseOnBoard: false,
        currentForSaleListing: 0,
        currentForRentListing: 0,
        closedSellerTransactions2025: 0,
        closedSellerTransactions2024: 0,
        closedSellerTransactions2023: 0,
        closedSellerTransactions2022: 0,
        closedBuyerTransactions2025: 0,
        closedBuyerTransactions2024: 0,
        closedBuyerTransactions2023: 0,
        closedBuyerTransactions2022: 0,
        closedLandlordTransactions2025: 0,
        closedLandlordTransactions2024: 0,
        closedLandlordTransactions2023: 0,
        closedLandlordTransactions2022: 0,
        closedTenantTransactions2025: 0,
        closedTenantTransactions2024: 0,
        closedTenantTransactions2023: 0,
        closedTenantTransactions2022: 0,
      });
      setEditingId(null);
    },
  });

  const handleAddNew = () => {
    setEditingId(null);
    setSelectedAgent({
      firstName: "",
      middleName: "",
      lastName: "",
      nameSuffix: "",
      title: "",
      homeAddress: "",
      officeAddress: "",
      notes: "",
      residentOwner: false,
      residentTenant: false,
      ownsOtherHomes: false,
      agentOnBoard: false,
      spouseOnBoard: false,
      currentForSaleListing: 0,
      currentForRentListing: 0,
      closedSellerTransactions2025: 0,
      closedSellerTransactions2024: 0,
      closedSellerTransactions2023: 0,
      closedSellerTransactions2022: 0,
      closedBuyerTransactions2025: 0,
      closedBuyerTransactions2024: 0,
      closedBuyerTransactions2023: 0,
      closedBuyerTransactions2022: 0,
      closedLandlordTransactions2025: 0,
      closedLandlordTransactions2024: 0,
      closedLandlordTransactions2023: 0,
      closedLandlordTransactions2022: 0,
      closedTenantTransactions2025: 0,
      closedTenantTransactions2024: 0,
      closedTenantTransactions2023: 0,
      closedTenantTransactions2022: 0,
    });
    setVisible(true);
  };

  const handleEdit = (agent) => {
    setEditingId(agent.id);
    setSelectedAgent(agent);
    setVisible(true);
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    deleteAgent(id);
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-pencil"
          rounded
          outlined
          className="p-button-warning"
          onClick={() => handleEdit(rowData)}
          disabled={updateAgentLoading}
        />
        <Button
          icon="pi pi-trash"
          rounded
          outlined
          className="p-button-danger"
          onClick={() => handleDelete(rowData.id)}
          disabled={deleteAgentLoading}
        />
      </div>
    );
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setGlobalFilterValue(value);
    setFilters({
      global: { value, matchMode: FilterMatchMode.CONTAINS },
    });
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      position: 1,
      render: (_, record) => {
        const name = [
          record.firstName,
          record.middleName,
          record.lastName,
          record.nameSuffix,
        ]
          .filter(Boolean)
          .join(" ");
        return name || "N/A";
      },
    },
    {
      title: "Resident Owner",
      dataIndex: "residentOwner",
      key: "residentOwner",
      position: 2,
      render: (value) => (value ? "Yes" : "No"),
    },
    {
      title: "Resident Tenant",
      dataIndex: "residentTenant",
      key: "residentTenant",
      position: 3,
      render: (value) => (value ? "Yes" : "No"),
    },
    {
      title: "Owns Other",
      dataIndex: "ownsOtherHomes",
      key: "ownsOtherHomes",
      position: 4,
      render: (value) => (value ? "Yes" : "No"),
    },
    {
      title: "Agent on Board",
      dataIndex: "agentOnBoard",
      key: "agentOnBoard",
      position: 5,
      render: (value) => (value ? "Yes" : "No"),
    },
    {
      title: "Spouse on Board",
      dataIndex: "spouseOnBoard",
      key: "spouseOnBoard",
      position: 6,
      render: (value) => (value ? "Yes" : "No"),
    },
    {
      title: "Current For Sale",
      dataIndex: "currentForSaleListing",
      key: "currentForSaleListing",
      position: 7,
      type: "number",
    },
    {
      title: "Current For Rent",
      dataIndex: "currentForRentListing",
      key: "currentForRentListing",
      position: 8,
      type: "number",
    },
  ];

  if (isLoading) {
    return (
      <div className="flex h-screen justify-center items-center">
        <i
          className="pi pi-spin pi-spinner text-red-700"
          style={{ fontSize: "2rem" }}
        ></i>
      </div>
    );
  }

  return (
    <div className="p-4">
      <Toast position="top-right" />
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Competing Agents</h2>
        <Button
          label="Add New Agent"
          icon="pi pi-plus"
          onClick={handleAddNew}
          className="p-button-success"
        />
      </div>

      <div className="mb-4">
        <span className="p-input-icon-left flex items-center gap-2">
          <IoIosSearch className="text-gray-500" />
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Search agents..."
            className="p-2 border border-gray-300 rounded w-full"
          />
        </span>
      </div>

      <DataTable
        value={agents}
        paginator
        rows={10}
        dataKey="id"
        filters={filters}
        globalFilterFields={["firstName", "lastName", "email"]}
        emptyMessage="No competing agents found"
        tableStyle={HideSortIcons}
        className="p-datatable-sm"
      >
        {columns.map((col) => (
          <Column
            key={col.key}
            field={col.dataIndex}
            header={col.title}
            body={col.render}
            sortable={false}
          />
        ))}
        <Column
          body={actionBodyTemplate}
          header="Actions"
          style={{ width: "150px" }}
        />
      </DataTable>

      {/* Add/Edit Dialog */}
      <Dialog
        visible={visible}
        onHide={() => setVisible(false)}
        header={editingId ? "Edit competing agent" : "Add competing agent"}
        modal
        style={{ width: "90vw", maxWidth: "1200px" }}
      >
        <form onSubmit={agentFormik.handleSubmit} className="space-y-6">
          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Form.Item label="First name *" layout="vertical">
              <Input
                name="firstName"
                placeholder="First name"
                value={agentFormik.values.firstName}
                onChange={agentFormik.handleChange}
                status={
                  agentFormik.touched.firstName && agentFormik.errors.firstName
                    ? "error"
                    : ""
                }
              />
              {agentFormik.touched.firstName &&
                agentFormik.errors.firstName && (
                  <span className="text-red-500 text-sm">
                    {agentFormik.errors.firstName}
                  </span>
                )}
            </Form.Item>

            <Form.Item label="Middle name" layout="vertical">
              <Input
                name="middleName"
                placeholder="Middle name"
                value={agentFormik.values.middleName}
                onChange={agentFormik.handleChange}
              />
            </Form.Item>

            <Form.Item label="Last name *" layout="vertical">
              <Input
                name="lastName"
                placeholder="Last name"
                value={agentFormik.values.lastName}
                onChange={agentFormik.handleChange}
                status={
                  agentFormik.touched.lastName && agentFormik.errors.lastName
                    ? "error"
                    : ""
                }
              />
              {agentFormik.touched.lastName && agentFormik.errors.lastName && (
                <span className="text-red-500 text-sm">
                  {agentFormik.errors.lastName}
                </span>
              )}
            </Form.Item>

            <Form.Item label="Name suffix" layout="vertical">
              <select
                name="nameSuffix"
                value={agentFormik.values.nameSuffix || ""}
                onChange={(e) =>
                  agentFormik.setFieldValue("nameSuffix", e.target.value)
                }
                className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-blue-200 focus:border-blue-400 bg-white"
              >
                <option value="">Select Suffix</option>
                <option value="Jr">Jr</option>
                <option value="Sr">Sr</option>
                <option value="I">I</option>
                <option value="II">II</option>
                <option value="III">III</option>
                <option value="IV">IV</option>
                <option value="V">V</option>
              </select>
            </Form.Item>
          </div>

          {/* Title */}
          <Form.Item label="Title" layout="vertical">
            <select
              name="title"
              value={agentFormik.values.title || ""}
              onChange={(e) =>
                agentFormik.setFieldValue("title", e.target.value)
              }
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-blue-200 focus:border-blue-400 bg-white"
            >
              <option value="">Select Title</option>
              <option value="Mr.">Mr.</option>
              <option value="Mrs.">Mrs.</option>
              <option value="Ms.">Ms.</option>
              <option value="Miss">Miss</option>
            </select>
          </Form.Item>

          {/* Home Address */}
          <Form.Item label="Home address" layout="vertical">
            <Input
              name="homeAddress"
              placeholder="Home address"
              value={agentFormik.values.homeAddress}
              onChange={agentFormik.handleChange}
            />
          </Form.Item>

          {/* Office Address */}
          <Form.Item label="Office address" layout="vertical">
            <Input
              name="officeAddress"
              placeholder="Office address"
              value={agentFormik.values.officeAddress}
              onChange={agentFormik.handleChange}
            />
          </Form.Item>

          {/* Resident Status */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Form.Item layout="vertical">
              <Checkbox
                name="residentOwner"
                checked={agentFormik.values.residentOwner}
                onChange={agentFormik.handleChange}
              >
                Resident owner
              </Checkbox>
            </Form.Item>

            <Form.Item layout="vertical">
              <Checkbox
                name="residentTenant"
                checked={agentFormik.values.residentTenant}
                onChange={agentFormik.handleChange}
              >
                Resident tenant
              </Checkbox>
            </Form.Item>

            <Form.Item layout="vertical">
              <Checkbox
                name="ownsOtherHomes"
                checked={agentFormik.values.ownsOtherHomes}
                onChange={agentFormik.handleChange}
              >
                Owns other homes
              </Checkbox>
            </Form.Item>

            <Form.Item layout="vertical">
              <Checkbox
                name="agentOnBoard"
                checked={agentFormik.values.agentOnBoard}
                onChange={agentFormik.handleChange}
              >
                Agent on board
              </Checkbox>
            </Form.Item>

            <Form.Item layout="vertical">
              <Checkbox
                name="spouseOnBoard"
                checked={agentFormik.values.spouseOnBoard}
                onChange={agentFormik.handleChange}
              >
                Spouse on board
              </Checkbox>
            </Form.Item>
          </div>

          {/* Current Listings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item label="Current for sale listings" layout="vertical">
              <InputNumber
                name="currentForSaleListing"
                value={agentFormik.values.currentForSaleListing}
                onChange={(value) =>
                  agentFormik.setFieldValue("currentForSaleListing", value || 0)
                }
                min={0}
              />
            </Form.Item>

            <Form.Item label="Current for rent listings" layout="vertical">
              <InputNumber
                name="currentForRentListing"
                value={agentFormik.values.currentForRentListing}
                onChange={(value) =>
                  agentFormik.setFieldValue("currentForRentListing", value || 0)
                }
                min={0}
              />
            </Form.Item>
          </div>

          {/* Transaction History - Seller */}
          <div className="bg-gray-50 p-4 rounded">
            <h4 className="font-bold text-gray-800 mb-3">
              Closed seller transactions
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[2025, 2024, 2023, 2022].map((year) => (
                <Form.Item
                  key={`seller-${year}`}
                  label={year}
                  layout="vertical"
                >
                  <InputNumber
                    name={`closedSellerTransactions${year}`}
                    value={
                      agentFormik.values[`closedSellerTransactions${year}`]
                    }
                    onChange={(value) =>
                      agentFormik.setFieldValue(
                        `closedSellerTransactions${year}`,
                        value || 0
                      )
                    }
                    min={0}
                  />
                </Form.Item>
              ))}
            </div>
          </div>

          {/* Transaction History - Buyer */}
          <div className="bg-gray-50 p-4 rounded">
            <h4 className="font-bold text-gray-800 mb-3">
              Closed buyer transactions
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[2025, 2024, 2023, 2022].map((year) => (
                <Form.Item key={`buyer-${year}`} label={year} layout="vertical">
                  <InputNumber
                    name={`closedBuyerTransactions${year}`}
                    value={agentFormik.values[`closedBuyerTransactions${year}`]}
                    onChange={(value) =>
                      agentFormik.setFieldValue(
                        `closedBuyerTransactions${year}`,
                        value || 0
                      )
                    }
                    min={0}
                  />
                </Form.Item>
              ))}
            </div>
          </div>

          {/* Transaction History - Landlord */}
          <div className="bg-gray-50 p-4 rounded">
            <h4 className="font-bold text-gray-800 mb-3">
              Closed landlord transactions
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[2025, 2024, 2023, 2022].map((year) => (
                <Form.Item
                  key={`landlord-${year}`}
                  label={year}
                  layout="vertical"
                >
                  <InputNumber
                    name={`closedLandlordTransactions${year}`}
                    value={
                      agentFormik.values[`closedLandlordTransactions${year}`]
                    }
                    onChange={(value) =>
                      agentFormik.setFieldValue(
                        `closedLandlordTransactions${year}`,
                        value || 0
                      )
                    }
                    min={0}
                  />
                </Form.Item>
              ))}
            </div>
          </div>

          {/* Transaction History - Tenant */}
          <div className="bg-gray-50 p-4 rounded">
            <h4 className="font-bold text-gray-800 mb-3">
              Closed tenant transactions
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[2025, 2024, 2023, 2022].map((year) => (
                <Form.Item
                  key={`tenant-${year}`}
                  label={year}
                  layout="vertical"
                >
                  <InputNumber
                    name={`closedTenantTransactions${year}`}
                    value={
                      agentFormik.values[`closedTenantTransactions${year}`]
                    }
                    onChange={(value) =>
                      agentFormik.setFieldValue(
                        `closedTenantTransactions${year}`,
                        value || 0
                      )
                    }
                    min={0}
                  />
                </Form.Item>
              ))}
            </div>
          </div>

          {/* Notes */}
          <Form.Item label="Notes" layout="vertical">
            <Input.TextArea
              name="notes"
              placeholder="Notes"
              rows={3}
              value={agentFormik.values.notes}
              onChange={agentFormik.handleChange}
            />
          </Form.Item>

          {/* Buttons */}
          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setVisible(false)}
              className="p-button-secondary"
            />
            <Button
              label={editingId ? "Update" : "Create"}
              icon="pi pi-check"
              type="submit"
              loading={createAgentLoading || updateAgentLoading}
              className="p-button-success"
            />
          </div>
        </form>
      </Dialog>
    </div>
  );
};

export default CompetingAgentManagement;
